import { Archive, ArchiveRestore, ArrowLeft, MoreHorizontal, Pencil } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { clearComboboxCache } from '@/components/combobox';
import { MediaRefImage } from '@/components/media-ref-image';
import { ProjectDialog, type ProjectValues } from '@/components/project-dialog';
import { ResponsiveTable } from '@/components/responsive-table';
import { RichText } from '@/components/rich-text';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useProject, useProposals } from '@/lib/api-hooks';
import { projectStatusStyles } from '@/lib/project-status';
import { archiveProject, restoreProject, updateProject } from '@/lib/projects';
import { proposalStatusStyles } from '@/lib/proposal-status';
import type { Proposal } from '@/lib/proposals';
import { invalidateResource } from '@/lib/resource';
import { cn } from '@/lib/utils';

export function ProjectDetailPage() {
  const { slug } = useParams();
  const { data: project, isLoading } = useProject(slug);
  const { data: proposalsData } = useProposals({ project: slug, pageSize: 100 });
  const proposals = proposalsData?.items ?? [];
  const [editOpen, setEditOpen] = useState(false);

  async function handleEdit(values: ProjectValues) {
    if (!project) return;
    await updateProject(project.id, values);
    clearComboboxCache('projects');
    invalidateResource('project');
    invalidateResource('projects');
  }

  async function handleArchive() {
    if (!project) return;
    await archiveProject(project.id);
    clearComboboxCache('projects');
    invalidateResource('project');
  }

  async function handleRestore() {
    if (!project) return;
    await restoreProject(project.id);
    clearComboboxCache('projects');
    invalidateResource('project');
  }

  if (isLoading) {
    return <p className="text-muted-foreground text-sm">Loading project…</p>;
  }

  if (!project) {
    return (
      <div className="flex flex-col items-start gap-4">
        <Link
          to="/projects"
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to projects
        </Link>
        <p className="text-muted-foreground text-sm">Project not found.</p>
      </div>
    );
  }

  const stats = [
    { label: 'Votes', value: project.votes.toLocaleString() },
    { label: 'Artists', value: String(project.artists) },
    { label: 'Voting period', value: project.period },
    { label: 'Updated', value: project.updated },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <Link
          to="/projects"
          className="inline-flex w-fit items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to projects
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="size-4" />
            Edit
          </Button>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger
              aria-label="Project actions"
              className="inline-flex size-9 items-center justify-center rounded-md border text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <MoreHorizontal className="size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {project.status === 'Archive' ? (
                <DropdownMenuItem onSelect={handleRestore}>
                  <ArchiveRestore />
                  Restore
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onSelect={handleArchive}>
                  <Archive />
                  Archive
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-semibold text-2xl tracking-tight">{project.name}</h1>
          <Badge variant="outline" className={cn(projectStatusStyles[project.status])}>
            {project.status}
          </Badge>
        </div>
        <RichText html={project.description} className="max-w-2xl text-muted-foreground" />
      </div>

      {(project.heroMedia || (project.gallery?.length ?? 0) > 0) && (
        <div className="flex flex-col gap-4">
          {project.heroMedia && (
            <MediaRefImage
              media={project.heroMedia}
              className="aspect-video w-full max-w-3xl rounded-lg border"
            />
          )}
          {(project.gallery?.length ?? 0) > 0 && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {project.gallery?.map((media) => (
                <MediaRefImage
                  key={media.id}
                  media={media}
                  className="aspect-video w-full rounded-md border"
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid gap-4 border-y py-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="space-y-1">
            <p className="text-muted-foreground text-sm">{stat.label}</p>
            <p className="font-medium text-base">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-3">
        <div className="space-y-1">
          <h2 className="font-semibold text-lg tracking-tight">Proposals</h2>
          <p className="text-muted-foreground text-sm">Submissions competing for this project.</p>
        </div>
        <ResponsiveTable
          rows={proposals}
          rowKey={(proposal) => proposal.id}
          emptyLabel="No proposals for this project yet."
          columns={[
            {
              key: 'proposal',
              header: 'Proposal',
              title: true,
              cell: (proposal: Proposal) => (
                <Link to={`/proposals/${proposal.slug}`} className="font-medium hover:underline">
                  {proposal.title}
                </Link>
              ),
            },
            {
              key: 'artist',
              header: 'Artist',
              cell: (proposal: Proposal) => (
                <Link
                  to={`/artists/${proposal.artistSlug}`}
                  className="hover:text-foreground hover:underline"
                >
                  {proposal.artist}
                </Link>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              cell: (proposal: Proposal) => (
                <Badge variant="outline" className={cn(proposalStatusStyles[proposal.status])}>
                  {proposal.status}
                </Badge>
              ),
            },
            {
              key: 'votes',
              header: 'Votes',
              align: 'right',
              cell: (proposal: Proposal) =>
                proposal.votes > 0 ? proposal.votes.toLocaleString() : '—',
            },
          ]}
        />
      </div>

      {editOpen && (
        <ProjectDialog
          open
          onOpenChange={setEditOpen}
          mode="edit"
          initialValues={{
            name: project.name,
            status: project.status,
            description: project.description,
            heroMedia: project.heroMedia ?? null,
            gallery: project.gallery ?? [],
          }}
          onSubmit={handleEdit}
        />
      )}
    </div>
  );
}
